// ContentBrowser — top-level shell. Two-pane: folder tree (left) + asset
// grid (right) + Inspector (far right). Owns the upload queue and the
// currently-mounted wizard.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import FolderTree from './FolderTree'
import AssetGrid, { deleteOne } from './AssetGrid'
import Inspector from './Inspector'
import NewAssetMenu from './NewAssetMenu'
import GenerateBodyAnimWizard, { GENERATE_WINDOW_ID } from './wizards/GenerateBodyAnimWizard'
import { useWindowState } from '../FloatingWindow'
import PresenceBar from '../collab/PresenceBar.jsx'
import { assetInspectorHost } from '../../lib/assetInspectorHost.js'
import { generatePanelHost } from '../../lib/generatePanelHost.js'
import { assetRegistry } from '../../lib/assetRegistry'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { buildUploadQueue, getWizard, acceptAttribute } from '../../lib/assetUploadDispatch'
import { dialog } from '../../lib/dialog'
import { getAnimationUsedBy } from '../../lib/assetManagerApi.js'
import { clipDeleteBody } from './clipUsage.js'
import { listCharacters, getMaterialPresets } from '../../lib/assetManagerApi.js'
import { isMaterialKind, buildMaterialCreateBody } from '../../lib/materialCreate.js'
import { blankBlueprintGraphs } from '../../lib/blankBlueprintGraph.js'
import { resolveCharacterId } from '../../lib/characterState'
import { versionPanelStore } from '../../lib/versionPanelStore'
import { materialGraphStore } from '../../lib/materialGraphStore'
import { runMaterialMigration } from '../../lib/materialMigration.js'
import { reloadMaterialsEverywhere } from '../../lib/materialReloadBus.js'
import { pill, card, head, accentFill } from '../../lib/studioStyles.js'
import GltfKindStep from './wizards/GltfKindStep'
import './wizards' // side-effect: registers every wizard with the dispatcher

const C = {
  bg:       'var(--m-bg, #0c0e15)',
  border:   'var(--m-line, #1f2129)',
  text:     'var(--m-fg, #e1e1ed)',
  dim:      'var(--m-fg-2, #8f8fa2)',
  primary:  'var(--m-accent, #bec2ff)',
  warn:     '#facc15',
  danger:   '#f87171',
}

export default function ContentBrowser({ characterId, shellInspector = false, onCollapse = null }) {
  const [activePath, setActivePath] = useState([])
  const [queue, setQueue] = useState([])  // { file, wizardKey, path, candidates }[]
  const [standaloneWizard, setStandaloneWizard] = useState(null) // 'prefab' | 'rig-replace' | null
  // Docked Generate-animation panel (renders in the inspector pane, not a modal,
  // so the viewport stays visible while generating). { path, initialValues?,
  // modifyTarget? } | null. Mutually exclusive with the asset inspector.
  const [generatePanel, setGeneratePanel] = useState(null)
  const [selectedAssetId, setSelectedAssetId] = useState(null)

  // Docked-shell mode: the asset inspector renders in the SHELL's right panel
  // (portal via assetInspectorHost), not an internal column — one details
  // surface app-wide. The host's clear() lets the outliner evict an asset
  // selection when one of its rows is picked, and vice versa the active flag
  // tells the panel to stand its scene content down.
  useEffect(() => {
    if (!shellInspector) return
    return assetInspectorHost.registerClear(() => setSelectedAssetId(null))
  }, [shellInspector])
  useEffect(() => {
    if (!shellInspector) return
    assetInspectorHost.setActive(!!selectedAssetId)
    return () => assetInspectorHost.setActive(false)
  }, [shellInspector, selectedAssetId])
  // The Generate panel has its OWN shell region, whose existence is derived from
  // this flag. Raised only while DOCKED — floated or popped out it is not in the
  // shell, and claiming the region then would leave an empty column standing
  // beside the floating window.
  const genWin = useWindowState(GENERATE_WINDOW_ID)
  const genDocked = genWin.mode === 'docked'
  const genInPanel = !!generatePanel && genDocked
  useEffect(() => {
    if (!shellInspector) return
    generatePanelHost.setActive(genInPanel)
    return () => generatePanelHost.setActive(false)
  }, [shellInspector, genInPanel])
  // Re-render when the host target mounts, so the portal lands.
  const [, hostTick] = useState(0)
  useEffect(() => {
    if (!shellInspector) return
    return assetInspectorHost.subscribe(() => hostTick(t => t + 1))
  }, [shellInspector])
  useEffect(() => {
    if (!shellInspector) return
    return generatePanelHost.subscribe(() => hostTick(t => t + 1))
  }, [shellInspector])
  const [newAssetMenu, setNewAssetMenu] = useState(null) // { x, y, extras?, targetPath? } | null
  const [inspectorWidth, setInspectorWidth] = useState(() => {
    const raw = Number(typeof localStorage !== 'undefined' ? localStorage.getItem('aos-content-inspector-width') : 0)
    if (!Number.isFinite(raw) || raw <= 0) return 360
    return Math.max(260, Math.min(720, raw))
  })
  const fileInputRef = useRef(null)
  const [, force] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [converting, setConverting] = useState(false)

  useEffect(() => {
    if (!characterId) return
    assetRegistry.setCharacter(characterId)
    // Ensure the asset lists (materials/textures/animations/…) are fetched for
    // this character. In the docked app App.jsx already inits the manager; in
    // the undocked editor popup App.jsx is NOT mounted, so without this the
    // content browser would render empty folders + just the rig. ensureInit
    // dedupes so the docked window doesn't double-fetch.
    CustomAssetManager.ensureInit(characterId).catch(e =>
      console.warn('[ContentBrowser] asset manager init failed:', e?.message ?? e))
    // Seed the texture list from the character's materials blob. The Go
    // server doesn't expose a single-character GET — only the list
    // endpoint — so we fetch the list and pick out our row. Textures don't
    // have their own table; they live on the character row, which is why
    // we have to do this at all.
    let cancelled = false
    listCharacters()
      .then(list => {
        if (cancelled) return
        const char = Array.isArray(list) && list.find(c => c.id === characterId)
        if (char) assetRegistry.setRigInfo({ slug: characterId, rigPath: char.rigPath })
      })
      .catch(e => console.warn('[ContentBrowser] failed to seed rig info:', e))

    // Migrate any leftover localStorage folder-shadow entries onto the
    // server now that folder_path is a real column on every asset table.
    // Runs once per character load; idempotent if the shadow is empty.
    const migrateTimer = setTimeout(() => {
      if (!cancelled) assetRegistry.migrateShadowToServer()
    }, 1500)
    return () => { cancelled = true; clearTimeout(migrateTimer) }
  }, [characterId])

  useEffect(() => assetRegistry.subscribe(() => force(n => n + 1)), [])

  // ASYNC since buildUploadQueue content-sniffs a JSON to tell a blueprint
  // export from a face-clip (an extension cannot decide it — see
  // assetUploadDispatch). A read failure must not swallow the drop, so the
  // dispatcher never throws and the wizard reports the real problem.
  const enqueueFiles = useCallback(async (files, path) => {
    const items = await buildUploadQueue(files, path ?? activePath)
    setQueue(q => [...q, ...items])
  }, [activePath])

  // Drop onto a folder. assetRegistry.moveAsset owns the whole move: optimistic
  // shadow, the per-kind PATCH, verification that the server echoed the folder
  // it was asked for, and the 'error' badge + console.error when it did not. It
  // never throws, and a failed move keeps its shadow so it is not lost.
  const handleDropAssetOnFolder = useCallback(async (assetId, path) => {
    await assetRegistry.moveAsset(assetId, path)
  }, [])

  // ── "New asset" menu dispatch ─────────────────────────────────────────────
  // Same handler drives the toolbar [+] button, the right-click on any
  // folder in the tree, and the right-click on empty grid space. Every
  // entry point routes through this so the action set stays consistent.
  // The optional `targetPath` overrides activePath when the user invoked
  // the menu from a specific folder (e.g. right-clicked on /prefabs while
  // viewing /materials).
  const handleNewAsset = useCallback(async (kind, targetPath) => {
    const path = Array.isArray(targetPath) ? targetPath : activePath
    if (kind === 'upload') {
      fileInputRef.current?.click()
    } else if (kind === 'upload-environment') {
      // Open file input for environment upload
      fileInputRef.current?.click()
    } else if (kind === 'folder') {
      const tentative = [...path, 'new folder']
      assetRegistry.createFolder(tentative)
      // The tree itself doesn't auto-rename — but we navigate INTO the new
      // folder so the artist sees it took effect.
      setActivePath(tentative)
    } else if (kind === 'prefab') {
      setStandaloneWizard('prefab')
    } else if (kind.startsWith('blueprint-')) {
      // ENG-1030: a row is ONE graph, so the menu names which — the question
      // "what kind?" is answered before anything is created rather than after.
      //
      // NEW MEANS NEW. This used to copy whatever the editor was holding for
      // that surface, which made "New → Blueprint → Body graph" a clone of the
      // character's current body graph under a name that said otherwise. The
      // reasoning was that an EMPTY graph breaks the character if anyone makes
      // it the default (ENG-991) — true, and an argument for never shipping an
      // empty graph, not for copying. Duplicate already exists for copying, in
      // this asset's own inspector, and it copies from the SERVER row rather
      // than the editor cache, which is the correct source for "copy this card"
      // and another sign New should not have been doing it.
      //
      // blankBlueprintGraphs is the empty-but-VALID graph — the same shapes a
      // brand-new character is given — so there is nothing to copy, nothing to
      // fail, and no dependence on what the editor happens to hold.
      const graphKind = kind.slice('blueprint-'.length)
      try {
        const charId = characterId ?? resolveCharacterId()
        if (!charId) throw new Error('No character is selected.')
        const graph = blankBlueprintGraphs()[graphKind]
        if (!graph) throw new Error(`Unknown blueprint kind "${graphKind}".`)

        const folderPath = path.length ? '/' + path.join('/') : '/blueprints'
        const row = await CustomAssetManager.createBlueprintAsset({
          name: `New ${graphKind} graph`, kind: graphKind, folderPath, graph,
        })
        setActivePath(folderPath.split('/').filter(Boolean))
        setSelectedAssetId(`blueprint_asset:${row.id}`)
      } catch (e) {
        await dialog.alert({
          title: 'Create blueprint failed',
          body: e?.message ?? String(e),
          variant: 'error',
        })
      }
    } else if (kind === 'generate-anim') {
      setSelectedAssetId(null) // free the shared far-right slot for the panel
      // nonce keys a fresh wizard remount so a prior Modify can't leave stale state.
      setGeneratePanel({ path, nonce: Date.now() })
    } else if (kind === 'ogs') {
      setStandaloneWizard('ogs')
    } else if (kind === 'rig') {
      setStandaloneWizard('rig-replace')
    } else if (isMaterialKind(kind)) {
      try {
        const folderPath = path.length ? '/' + path.join('/') : '/materials'
        const presets = kind === 'material-graph' ? null : (await getMaterialPresets()).presets
        const body = buildMaterialCreateBody(kind, presets, { folderPath })
        const row = await CustomAssetManager.createMaterial(body)
        const parts = folderPath.split('/').filter(Boolean)
        setActivePath(parts)
        setSelectedAssetId(`material:${row.id}`)
      } catch (e) {
        await dialog.alert({ title: 'Create material failed', body: e?.message ?? String(e), variant: 'error' })
      }
    }
  }, [activePath, characterId])

  // ── Toolbar trash: delete the inspected asset ─────────────────────────────
  const trashSelected = async () => {
    if (!selectedAssetId) return
    const a = assetRegistry.list().find(x => x.id === selectedAssetId)
    if (!a) return
    const body = await clipDeleteBody([a], `Delete "${a.name}"?`, getAnimationUsedBy)
    const ok = await dialog.confirm({
      title: 'Delete asset?',
      body,
      confirmLabel: 'Delete',
      danger: true,
    })
    if (!ok) return
    try {
      await deleteOne(a)
      setSelectedAssetId(null)
    } catch (e) {
      await dialog.alert({
        title: 'Delete failed',
        body: e?.message ?? String(e),
        variant: 'error',
      })
    }
  }

  // ── Toolbar refresh: re-pull assets from the server without a page reload ──
  // Picks up clips added out-of-band (synced to the cloud from another tool)
  // and registers them on the live mixer so they're immediately selectable and
  // loopable in the blueprint.
  const refreshAssets = async () => {
    setRefreshing(true)
    try {
      await CustomAssetManager.refresh()
    } catch (e) {
      await dialog.alert({
        title: 'Refresh failed',
        body: e?.message ?? String(e),
        variant: 'error',
      })
    } finally {
      setRefreshing(false)
    }
  }

  // ── Toolbar convert: server-side dry-run report → confirm → run → reload ──
  // The dry run is a whole-catalogue round trip, so the button reports it the
  // same way Refresh above does — disabled with a busy glyph while in flight,
  // which also stops two clicks stacking two reports and two confirms. A
  // rejection anywhere in the chain is a dialog, never an unhandled rejection
  // with nothing on screen.
  const convertMaterials = async () => {
    setConverting(true)
    try {
      await runMaterialMigration({
        migrate: (o) => CustomAssetManager.migrateMaterialsToGraph(o),
        confirm: (o) => dialog.confirm(o),
        alert: (o) => dialog.alert(o),
        reload: () => reloadMaterialsEverywhere(),
      })
    } catch (e) {
      await dialog.alert({
        title: 'Convert materials failed',
        body: e?.message ?? String(e),
        variant: 'error',
      })
    } finally {
      setConverting(false)
    }
  }

  // ── Wizard rendering ──────────────────────────────────────────────────────
  const head = queue[0]
  const advance = () => setQueue(q => q.slice(1))

  const renderWizard = () => {
    if (!head) return null
    if (head.wizardKey === 'unknown-extension') {
      return (
        <SimpleModal
          title="Unsupported file"
          message={`No wizard registered for "${head.file.name}". Add the extension to assetUploadDispatch.`}
          onClose={advance}
        />
      )
    }
    if (head.wizardKey === 'ambiguous-extension') {
      return (
        <SimpleModal
          title="Cannot tell what this file is"
          message={`"${head.file.name}" could be ${head.candidates.map(k => getWizard(k)?.label ?? k).join(' or ')}, and nothing in the file says which. Upload it from the matching New-asset entry instead.`}
          onClose={advance}
        />
      )
    }
    // A dropped glTF is ASKED about, never filed (ENG-1108). The dialog hands
    // back the wizard key the user confirmed and we REWRITE the queue head in
    // place, so the chosen uploader mounts next with the same file and path.
    // Rewriting rather than pushing keeps the queue's length honest: one
    // dropped file is still one entry, and Cancel still drops exactly it.
    if (head.wizardKey === 'gltf-kind') {
      return (
        <GltfKindStep
          file={head.file}
          detected={head.detected}
          onChoose={(wizardKey) => setQueue(q => [{ ...q[0], wizardKey }, ...q.slice(1)])}
          onCancel={advance}
        />
      )
    }
    const reg = getWizard(head.wizardKey)
    const Comp = reg?.component
    if (!Comp) {
      // This used to be a console.warn + silent advance, which DISCARDED the
      // user's file with no visible trace — the failure mode a dropped
      // ambiguous extension hit, since 'disambiguate-glb' never had a
      // component registered. A dropped file must never vanish quietly.
      console.error('[ContentBrowser] no component for wizard key', head.wizardKey)
      return (
        <SimpleModal
          title="Cannot import this file"
          message={`"${head.file.name}" was not imported: no uploader is registered for "${head.wizardKey}". Nothing was changed.`}
          onClose={advance}
        />
      )
    }
    return (
      <Comp
        file={head.file}
        path={head.path}
        onDone={(result) => {
          advance()
          // A blueprint import mints a NEW card; select it so the import is
          // visibly a thing that happened rather than a dialog that closed.
          if (result?.row?.id && head.wizardKey === 'blueprint-import') {
            setActivePath((head.path ?? []).length ? head.path : ['blueprints'])
            setSelectedAssetId(`blueprint_asset:${result.row.id}`)
          }
        }}
        onCancel={advance}
      />
    )
  }

  const renderStandaloneWizard = () => {
    if (!standaloneWizard) return null
    const reg = getWizard(standaloneWizard)
    const Comp = reg?.component
    if (!Comp) { setStandaloneWizard(null); return null }
    return (
      <Comp
        path={activePath}
        onDone={() => setStandaloneWizard(null)}
        onCancel={() => setStandaloneWizard(null)}
      />
    )
  }

  const staleBackend = assetRegistry.isStaleBackend()
  const trashEnabled = !!selectedAssetId

  return (
    <div style={{
      width: '100%', height: '100%',
      background: C.bg, color: C.text,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', position: 'relative',
    }}>
      {staleBackend && (
        <div style={{
          background: '#3a2e08', borderBottom: `1px solid ${C.warn}55`,
          color: C.warn, fontFamily: 'var(--m-font)', fontSize: 11,
          padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span>⚠ Folder layout was authored against a different backend.</span>
          <button onClick={() => assetRegistry.resolveStaleBackend('apply')} style={banBtn}>Apply anyway</button>
          <button onClick={() => assetRegistry.resolveStaleBackend('discard')} style={banBtn}>Discard</button>
        </div>
      )}

      {/* Toolbar: [+] new-asset menu, [trash] delete selected. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderBottom: `1px solid ${C.border}`,
        background: 'var(--m-panel, #080a10)',
      }}>
        {/* Docked in the shell, THIS is the dock's header: the label doubles as
            the collapse control so there is one ASSETS row, not two stacked
            ones. Undocked (popup / legacy host) it stays a plain title. */}
        {onCollapse ? (
          <span
            data-testid="content-browser-collapse"
            onClick={onCollapse}
            title="Collapse assets (⌥A)"
            style={{
              ...head, marginRight: 6,
              cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
            }}
          >▾ Assets</span>
        ) : (
          <span style={{
            ...head, marginRight: 6,
          }}>Assets</span>
        )}
        <IconBtn
          title="New asset"
          glyph="+"
          onClick={(ev) => {
            const r = ev.currentTarget.getBoundingClientRect()
            setNewAssetMenu({ x: r.left, y: r.bottom + 4 })
          }}
        />
        <IconBtn
          title={trashEnabled ? 'Delete selected' : 'Select an asset to delete'}
          glyph="🗑"
          disabled={!trashEnabled}
          onClick={trashSelected}
          danger
        />
        <IconBtn
          title="Refresh assets from server"
          glyph={refreshing ? '…' : '⟳'}
          disabled={refreshing}
          onClick={refreshAssets}
        />
        <IconBtn
          title="Convert materials to graph…"
          glyph={converting ? '…' : '⇄'}
          disabled={converting}
          onClick={convertMaterials}
        />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <PresenceBar characterId={characterId} />
          <IconBtn
            title="Version history"
            glyph="⎇"
            onClick={() => versionPanelStore.toggle()}
          />
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <FolderTree
          activePath={activePath}
          onSelectPath={setActivePath}
          onDropAsset={handleDropAssetOnFolder}
          onDropFiles={enqueueFiles}
          onCreateAtPath={(kind, path) => handleNewAsset(kind, path)}
        />
        <AssetGrid
              characterId={characterId}
              activePath={activePath}
              onUploadFiles={enqueueFiles}
              selectedAssetId={selectedAssetId}
              onSelectAsset={(id) => {
                setGeneratePanel(null) // free the pane for the inspector
                setSelectedAssetId(id)
                // Memo the asset for the ← "use selected" buttons. Kinds that
                // are assignable to slots (material, texture) are tracked.
                const sel = assetRegistry.list().find(a => a.id === id)
                if (sel && (sel.kind === 'material' || sel.kind === 'texture')) {
                  assetRegistry.setLastSelected(sel.kind, sel.rawId)
                }
              }}
              onNavigate={setActivePath}
              onDropAsset={handleDropAssetOnFolder}
              onOpenGraph={(a) => materialGraphStore.open(a)}
              onEmptyContextMenu={(x, y, path) => setNewAssetMenu({
                x, y,
                targetPath: path,
                extras: [
                  { key: '__copy',  label: 'Copy',  hint: 'Ctrl+C', disabled: true },
                  { key: '__paste', label: 'Paste', hint: 'Ctrl+V', disabled: true },
                ],
              })}
            />
            {(() => {
          // Generate panel and inspector are mutually exclusive in the far-right slot.
          if (generatePanel) {
            const wizard = (
              <GenerateBodyAnimWizard
                key={generatePanel.nonce}
                path={generatePanel.path ?? activePath}
                width={shellInspector ? '100%' : inspectorWidth}
                hosted={shellInspector && genDocked}
                initialValues={generatePanel.initialValues ?? null}
                modifyTarget={generatePanel.modifyTarget ?? null}
                onDone={() => setGeneratePanel(null)}
                onCancel={() => setGeneratePanel(null)}
              />
            )
            // Shell mode: the same portal mechanic the asset inspector uses, but
            // into the Generate panel's OWN region (generatePanelHost) — the DOM
            // moves, every callback and all the generate/save state stays owned
            // right here. Deliberately not the inspector's host: both must be
            // readable at once, especially in Modify, which starts from a
            // selected clip whose properties you are working against.
            if (shellInspector) {
              if (!genDocked) return wizard // floating / popped out — no host needed
              const target = generatePanelHost.getTarget()
              return target ? createPortal(wizard, target) : null
            }
            return (
              <>
                <Splitter
                  width={inspectorWidth}
                  onChange={(w) => setInspectorWidth(w)}
                  onCommit={(w) => {
                    try { localStorage.setItem('aos-content-inspector-width', String(Math.round(w))) } catch {}
                  }}
                />
                {wizard}
              </>
            )
          }
          const selected = assetRegistry.list().find(a => a.id === selectedAssetId) ?? null
          if (!selected) return null
          const inspector = (
            <Inspector
              asset={selected}
              width={shellInspector ? '100%' : inspectorWidth}
              onClose={() => setSelectedAssetId(null)}
              onNavigateToAsset={(id) => {
                // 🔍 Find: focus the asset in the grid (matches Unreal's magnify).
                const target = assetRegistry.list().find(a => a.id === id)
                if (!target) return
                if (Array.isArray(target.path)) setActivePath(target.path)
                setSelectedAssetId(id)
              }}
              onOpenGraph={(a) => materialGraphStore.open(a)}
              onModifyGenerated={(initialValues, modifyTarget) => {
                // Reopen the Generate panel prefilled from this clip's provenance.
                setSelectedAssetId(null)
                setGeneratePanel({ path: modifyTarget.path ?? activePath, initialValues, modifyTarget, nonce: Date.now() })
              }}
            />
          )
          // Shell mode: the inspector's DOM lives in the shell's right panel;
          // all its state and callbacks stay right here.
          if (shellInspector) {
            const target = assetInspectorHost.getTarget()
            return target ? createPortal(inspector, target) : null
          }
          return (
            <>
              <Splitter
                width={inspectorWidth}
                onChange={(w) => setInspectorWidth(w)}
                onCommit={(w) => {
                  try { localStorage.setItem('aos-content-inspector-width', String(Math.round(w))) } catch {}
                }}
              />
              {inspector}
            </>
          )
        })()}
      </div>

      {/* Hidden file input that the "Upload" and "Upload environment" menu items trigger. */}
      <input
        ref={fileInputRef}
        data-testid="content-browser-file-input"
        type="file"
        multiple
        accept={acceptAttribute() /* derived from the wizard registry, never restated */}
        onChange={e => {
          const files = Array.from(e.target.files ?? [])
          if (files.length) enqueueFiles(files, activePath)
          e.target.value = '' // reset so picking the same file twice still fires
        }}
        style={{ display: 'none' }}
      />

      {newAssetMenu && (
        <NewAssetMenu
          x={newAssetMenu.x} y={newAssetMenu.y}
          extraItems={newAssetMenu.extras ?? []}
          onPick={(key) => {
            // Grid-empty-space extras (copy/paste) are no-ops for now.
            if (key === '__copy' || key === '__paste') return
            handleNewAsset(key, newAssetMenu.targetPath)
          }}
          onClose={() => setNewAssetMenu(null)}
        />
      )}
      {renderWizard()}
      {renderStandaloneWizard()}
    </div>
  )
}

function Splitter({ width, onChange, onCommit }) {
  const [hover, setHover] = React.useState(false)
  const [dragging, setDragging] = React.useState(false)

  const onPointerDown = (e) => {
    e.preventDefault()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    setDragging(true)
    // The Inspector lives to the right of this splitter and grows by pulling
    // left. Anchor on the splitter's own right edge at drag start so the
    // resulting width is just `anchorX - clientX` (clamped).
    const startRect = handle.getBoundingClientRect()
    const anchorRight = startRect.right
    let latest = width
    const onMove = (ev) => {
      const next = Math.max(260, Math.min(720, anchorRight - ev.clientX + width))
      // Above: at drag start, inspector right edge is `anchorRight + width`.
      // New width = inspectorRight - ev.clientX = (anchorRight + width) - ev.clientX.
      latest = next
      onChange(next)
    }
    const onUp = (ev) => {
      handle.releasePointerCapture(ev.pointerId)
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
      handle.removeEventListener('pointercancel', onUp)
      setDragging(false)
      onCommit?.(latest)
    }
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
    handle.addEventListener('pointercancel', onUp)
  }

  const active = hover || dragging
  return (
    <div
      onPointerDown={onPointerDown}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      title="Drag to resize inspector"
      style={{
        flexShrink: 0,
        width: 6,
        cursor: 'col-resize',
        background: active ? 'var(--m-accent, #4d5bff)' : 'var(--m-line, #1f2129)',
        opacity: active ? 0.7 : 1,
        touchAction: 'none',
        transition: dragging ? 'none' : 'background 120ms, opacity 120ms',
      }}
    />
  )
}

function IconBtn({ title, glyph, onClick, disabled, danger }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        border: `1px solid ${disabled ? 'var(--m-line, #1f2129)' : (danger ? '#f8717155' : 'var(--m-line-hi, #2a2c36)')}`,
        color: disabled ? 'var(--m-fg-3, #3a3c45)' : (danger ? '#fca5a5' : 'var(--m-accent, #bec2ff)'),
        borderRadius: '50%',
        width: 26, height: 26,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, padding: 0,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background var(--m-ease), color var(--m-ease)',
      }}
    >{glyph}</button>
  )
}

const banBtn = {
  background: 'transparent', border: '1px solid #facc1555',
  color: '#facc15', fontFamily: 'var(--m-font)', fontSize: 10,
  borderRadius: 999, padding: '2px 10px',
  letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer',
}

function SimpleModal({ title, message, onClose }) {
  return (
    <div
      onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(4,6,12,.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div style={{
        background: 'var(--m-panel, #11131b)', border: '1px solid var(--m-line-hi, #2a2c36)', ...card,
        padding: 20, width: 'min(440px, 90vw)',
        color: 'var(--m-fg, #e1e1ed)', fontFamily: 'var(--m-font)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--m-fg-2, #c5c5d9)', lineHeight: 1.5 }}>{message}</div>
        <div style={{ textAlign: 'right', marginTop: 14 }}>
          <button onClick={onClose} style={{
            ...accentFill, border: '1px solid var(--m-accent, #4d5bff)',
            fontFamily: 'var(--m-font)', fontSize: 11, padding: '5px 16px', ...pill,
            letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer',
          }}>OK</button>
        </div>
      </div>
    </div>
  )
}
